import { useState, useEffect, useMemo } from 'react';
import AnalyticsApi from 'libs/api/analytics';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';

const api = new AnalyticsApi();

const useFilters = (hideDate = false, hideInsegna = false, isAllReports) => {
  const { t } = useTranslation();
  let [searchParams, setSearchParams] = useSearchParams();
  const [title, setTitle] = useState('');
  const [filtersFromBackend, setFiltersFromBackend] = useState({});
  const [tradingCalendarId, setTradingCalendarId] = useState('');
  const [filters, setFilters] = useState(
    [
      {
        title: t('Periodo'),
        type: 'select',
        name: 'by_period',
        key: 'periods',
        values: [],
      },
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        key: 'shopping_centers',
        valueType: 'number',
        values: [],
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company',
        key: 'companies',
        valueType: 'number',
        values: [],
        allHide: hideInsegna,
      },
      {
        title: t('Periodo YTD Dal'),
        type: 'date',
        name: 'by_date_from_ytd',
        format: 'DD-MM-YYYY',
        keys: 'by_date_from_ytd',
      },
      {
        title: t('Week Dal'),
        type: 'select',
        name: 'by_week_from_ytd',
        key: 'by_week_from_ytd',
        hide: true,
        showType: 'week',
        valueType: 'number',
        values: [],
      },
      {
        title: t('Periodo YTD Al'),
        type: 'date',
        name: 'by_date_to_ytd',
        format: 'DD-MM-YYYY',
        keys: 'by_date_to_ytd',
      },
      {
        title: t('Week Al'),
        type: 'select',
        name: 'by_week_to_ytd',
        key: 'by_week_to_ytd',
        hide: true,
        showType: 'week',
        valueType: 'number',
        values: [],
      },

      {
        title: t('Periodo LY Dal'),
        type: 'date',
        name: 'by_date_from_ly',
        format: 'DD-MM-YYYY',
        keys: 'by_date_from_ly',
      },
      {
        title: t('Week Dal'),
        type: 'select',
        name: 'from_week_ly',
        key: 'from_week_ly',
        hide: true,
        showType: 'week',
        valueType: 'number',
        values: [],
      },
      {
        title: t('Periodo LY Al'),
        type: 'date',
        name: 'by_date_to_ly',
        format: 'DD-MM-YYYY',
        keys: 'by_date_to_ly',
      },
      {
        title: t('Week Al'),
        type: 'select',
        name: 'to_week_ly',
        showType: 'week',
        hide: true,
        key: 'to_week_ly',
        valueType: 'number',
        values: [],
      },
      {
        title: t('Like for like'),
        type: 'select',
        name: 'by_like_for_like',
        key: 'like_for_like',
        valueType: 'number',
        values: [],
      },
      {
        title: t('Trading calendar'),
        type: 'select',
        name: 'by_trading_calendar',
        key: 'trading_calendar',
        valueType: 'number',
        values: [],
      },
      {
        title: t('Giorno'),
        type: 'select',
        name: 'by_day_type',
        key: 'by_day_type',
        values: [],
      },
      {
        title: t('MACRO-CATEGORIA'),
        type: 'select',
        name: 'by_macro_category',
        key: 'macro_category',
        valueType: 'number',
        values: [],
      },
      {
        title: t('CATEGORIA MERCEOLOGICA'),
        type: 'select',
        name: 'by_company_category',
        key: 'company_category',
        valueType: 'number',
        values: [],
      },
      {
        title: t('SOTTOCATEGORIA'),
        type: 'select',
        name: 'by_commodity_subcategory',
        key: 'company_child_categories',
        values: [],
      },
      {
        title: t('Segmento'),
        type: 'select',
        name: 'by_prestige_level',
        key: 'prestige_levels',
        valueType: 'number',
        values: [],
      },
    ],
    [hideInsegna],
  );

  useEffect(() => {
    (async () => {
      if (isAllReports) return;
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      try {
        const {
          data: { filters: filtersFromBackend, title },
        } = await api.getFilters({ params });
        setFiltersFromBackend(filtersFromBackend);
        setTradingCalendarId(searchParams.get('by_trading_calendar'));
        const newFilters = filters.map((i) => {
          if (i.type === 'select' && filtersFromBackend[i.key]) {
            return {
              ...i,
              values: filtersFromBackend[i.key],
              hide: i.showType === 'week' && params.by_period !== 'week',
            };
          }
          if (i.type === 'select') {
            return {
              ...i,
              hide: i.showType === 'week' && params.by_period !== 'week',
            };
          }
          if (params.by_period === 'day') {
            return {
              ...i,
              format: 'DD-MM-YYYY',
              showFormat: 'DD-MMMM-YYYY',
              picker: 'date',
            };
          }

          if (params.by_period === 'week') {
            return {
              ...i,
              picker: 'year',
              format: 'DD-MM-YYYY',
              showFormat: 'YYYY',
            };
          }

          if (params.by_period === 'month') {
            return {
              ...i,
              picker: 'month',
              format: 'DD-MM-YYYY',
              showFormat: 'MMMM-YYYY',
            };
          }

          if (params.by_period === 'year') {
            return {
              ...i,
              picker: 'year',
              format: 'DD-MM-YYYY',
              showFormat: 'YYYY',
            };
          }
          return { ...i };
        });
        setFilters(newFilters);
        setTitle(title);
      } catch (error) {
        console.log(error);
      }
    })();
  }, [searchParams, isAllReports]);

  useEffect(() => {
    if (!hideDate) {
      const currentSearchParams = Object.fromEntries([...searchParams]);
      if (!currentSearchParams.by_date_from_ytd) {
        currentSearchParams.by_date_from_ytd = moment()
          .startOf('month')
          .format('DD-MM-YYYY');
      }
      if (!currentSearchParams.by_date_to_ytd) {
        currentSearchParams.by_date_to_ytd = moment()
          .startOf('month')
          .add(1, 'month')
          .format('DD-MM-YYYY');
      }
      if (!currentSearchParams.by_date_from_ly) {
        currentSearchParams.by_date_from_ly = moment()
          .startOf('month')
          .subtract(1, 'year')
          .format('DD-MM-YYYY');
      }
      if (!currentSearchParams.by_date_to_ly) {
        currentSearchParams.by_date_to_ly = moment()
          .startOf('month')
          .add(1, 'month')
          .subtract(1, 'year')
          .format('DD-MM-YYYY');
      }
      if (!currentSearchParams.by_period) {
        currentSearchParams.by_period = '';
      }
      if (!currentSearchParams.by_shopping_center) {
        currentSearchParams.by_shopping_center = '';
      }
      if (!currentSearchParams.by_company) {
        currentSearchParams.by_company = '';
      }
      setSearchParams(currentSearchParams);
    }
  }, [hideDate]);

  useEffect(() => {
    const currentSearchParams = Object.fromEntries([...searchParams]);
    if (
      currentSearchParams.by_period === 'week' &&
      !currentSearchParams.hasOwnProperty('by_week_from_ytd')
    ) {
      currentSearchParams.by_week_from_ytd = '';
      currentSearchParams.by_week_to_ytd = '';
      currentSearchParams.from_week_ly = '';
      currentSearchParams.to_week_ly = '';
      setSearchParams(currentSearchParams);
    }
  }, [searchParams]);

  useEffect(() => {
    const currentSearchParams = Object.fromEntries([...searchParams]);
    if (filtersFromBackend.from_date_ly && tradingCalendarId) {
      currentSearchParams.by_date_from_ytd = moment(
        filtersFromBackend.from_date_ytd,
        'YYYY-MM-DD',
      ).format('DD-MM-YYYY');
      currentSearchParams.by_date_to_ytd = moment(
        filtersFromBackend.to_date_ytd,
        'YYYY-MM-DD',
      ).format('DD-MM-YYYY');
      currentSearchParams.by_date_from_ly = moment(
        filtersFromBackend.from_date_ly,
        'YYYY-MM-DD',
      ).format('DD-MM-YYYY');
      currentSearchParams.by_date_to_ly = moment(
        filtersFromBackend.to_date_ly,
        'YYYY-MM-DD',
      ).format('DD-MM-YYYY');
      setSearchParams(currentSearchParams);
    }
  }, [tradingCalendarId]);
  return {
    filters,
    title,
    searchParams,
  };
};

export default useFilters;
